'use client';

import { useState, useCallback } from 'react';
import { cn } from '@fshr/shared-react';
import { useTealium } from '@fshr/context/tealium-context';
import Image from '../image';
import { IconChevronLightLeft, IconChevronLightRight } from '../icon';
import type { SplitPageHeroImageData } from './types';

const MAX_DOTS = 5;

function visibleDotRange(totalCount: number, currentPage: number) {
	if (totalCount <= MAX_DOTS) {
		return Array.from({ length: totalCount }, (_, index) => index);
	}
	const start = Math.min(
		Math.max(currentPage - Math.floor(MAX_DOTS / 2), 0),
		totalCount - MAX_DOTS
	);
	return Array.from({ length: MAX_DOTS }, (_, index) => start + index);
}

function dotSizeClass(pageIndex: number, dots: number[], currentPage: number) {
	const distance = Math.abs(dots.indexOf(pageIndex) - dots.indexOf(currentPage));
	if (distance === 0) return 'size-[7px]';
	if (distance === 1) return 'size-[6px]';
	return 'size-[5px]';
}

type GalleryProps = {
	images?: SplitPageHeroImageData[];
	fallbackImage?: SplitPageHeroImageData;
	className?: string;
};

/**
 * BL AC "Crops — Apply existing cropping solution for the different viewports":
 * Desktop (XL) = 1:1, Desktop (L) = 4:5, Tablet (M) = 1:1, Mobile (S) = 1:1.
 *
 * Repo breakpoints (tailwind/global.css): md 700px, lg 960px, xl 1700px, so
 * Desktop (L) is the `lg` band below `xl` and every other band is square.
 */
const SQUARE_VISIBILITY = 'block lg:hidden xl:block';
const PORTRAIT_VISIBILITY = 'hidden lg:block xl:hidden';
const CROP_ASPECT = 'aspect-square lg:aspect-[4/5] xl:aspect-square';

function hasImage(image?: SplitPageHeroImageData): boolean {
	return Boolean(image?.squareImage || image?.portraitImage);
}

type SlideProps = {
	image: SplitPageHeroImageData;
	fallbackAlt: string;
};

/**
 * Renders the authored croppings. When only one cropping is authored it is used
 * at every viewport, so a partially configured gallery item still shows an image.
 */
function GallerySlide({ image, fallbackAlt }: SlideProps) {
	const { squareImage, portraitImage } = image;
	const hasBothCrops = Boolean(squareImage && portraitImage);

	return (
		<>
			{squareImage && (
				<Image
					roughSize="l"
					imagePrimitive={squareImage}
					alt={squareImage.alt ?? fallbackAlt}
					className={cn('h-full w-full object-cover', hasBothCrops ? SQUARE_VISIBILITY : 'block')}
				/>
			)}
			{portraitImage && (
				<Image
					roughSize="l"
					imagePrimitive={portraitImage}
					alt={portraitImage.alt ?? fallbackAlt}
					className={cn('h-full w-full object-cover', hasBothCrops ? PORTRAIT_VISIBILITY : 'block')}
				/>
			)}
		</>
	);
}

export function SplitPageHeroGallery({ images, fallbackImage, className }: GalleryProps) {
	const [currentPage, setCurrentPage] = useState(0);
	const { trackInteraction } = useTealium();

	const authoredImages = (images ?? []).filter(hasImage);

	// BL AC: "If product does not have Media Gallery items, fallback to Primary
	// Featured Image (page will then display only a single image vs carousel)".
	const resolvedImages =
		authoredImages.length > 0 ? authoredImages : hasImage(fallbackImage) ? [fallbackImage!] : [];

	const totalCount = resolvedImages.length;

	const goToPage = useCallback(
		(page: number) => {
			// UI AC 5d: "Navigation is circular."
			setCurrentPage((page + totalCount) % totalCount);
		},
		[totalCount]
	);

	const trackArrowClick = useCallback(
		() => trackInteraction('product detail page', 'curated image carousel', 'image carousel icon'),
		[trackInteraction]
	);

	const handlePrev = useCallback(() => {
		goToPage(currentPage - 1);
		trackArrowClick();
	}, [currentPage, goToPage, trackArrowClick]);
	const handleNext = useCallback(() => {
		goToPage(currentPage + 1);
		trackArrowClick();
	}, [currentPage, goToPage, trackArrowClick]);

	if (totalCount === 0) return null;

	// UI AC 5b: "If only 1 image is authored, no arrow controls are shown."
	if (totalCount === 1) {
		return (
			<div className={cn('relative w-full overflow-hidden rounded-lg', CROP_ASPECT, className)}>
				<GallerySlide image={resolvedImages[0]} fallbackAlt="Experience image" />
			</div>
		);
	}

	const visibleDots = visibleDotRange(totalCount, currentPage);

	return (
		<div className={cn('relative w-full overflow-hidden rounded-lg', CROP_ASPECT, className)}>
			<div className="relative h-full w-full">
				{resolvedImages.map((image, index) => (
					<div
						key={index}
						className={cn(
							'absolute inset-0 transition-opacity duration-300',
							index === currentPage ? 'opacity-100' : 'pointer-events-none opacity-0'
						)}
						aria-hidden={index !== currentPage}
					>
						<GallerySlide image={image} fallbackAlt={`Gallery image ${index + 1}`} />
					</div>
				))}

				<div className="group/arrows absolute inset-y-0 left-0 right-0 z-20 flex items-center justify-between px-inline-2xs">
					<button
						type="button"
						onClick={handlePrev}
						className="group/arrow flex size-[44px] items-center justify-center opacity-0 transition-opacity duration-200 focus-visible:opacity-100 group-hover/arrows:opacity-100 max-[959px]:opacity-100"
						aria-label="Previous image"
					>
						<span className="flex size-300 items-center justify-center rounded-full bg-button-on-image-primary shadow-[0px_5px_7.5px_rgba(3,3,3,0.12)] transition-colors group-hover/arrow:bg-button-on-image-primary-hover group-focus-visible/arrow:bg-button-on-image-primary-hover">
							<IconChevronLightLeft width={12} height={12} />
						</span>
					</button>
					<button
						type="button"
						onClick={handleNext}
						className="group/arrow flex size-[44px] items-center justify-center opacity-0 transition-opacity duration-200 focus-visible:opacity-100 group-hover/arrows:opacity-100 max-[959px]:opacity-100"
						aria-label="Next image"
					>
						<span className="flex size-300 items-center justify-center rounded-full bg-button-on-image-primary shadow-[0px_5px_7.5px_rgba(3,3,3,0.12)] transition-colors group-hover/arrow:bg-button-on-image-primary-hover group-focus-visible/arrow:bg-button-on-image-primary-hover">
							<IconChevronLightRight width={12} height={12} />
						</span>
					</button>
				</div>

				<div className="absolute left-1/2 top-100 z-20 flex -translate-x-1/2 items-center justify-center rounded-[100px] bg-white/60 p-100 backdrop-blur-[7.5px]">
					<div className="spacing-inline-2xs flex h-[7px] w-[56px] items-center justify-center overflow-clip">
						{visibleDots.map(pageIndex => {
							const isActive = pageIndex === currentPage;
							return (
								<button
									key={pageIndex}
									type="button"
									onClick={() => goToPage(pageIndex)}
									aria-label={`Go to image ${pageIndex + 1}`}
									aria-current={isActive}
									className={cn(
										'shrink-0 rounded-full transition-all duration-300',
										dotSizeClass(pageIndex, visibleDots, currentPage),
										isActive ? 'bg-[#030303]' : 'bg-[#6b6b6b] opacity-75'
									)}
								/>
							);
						})}
					</div>
					<span
						className="sr-only"
						aria-live="polite"
					>{`Image ${currentPage + 1} of ${totalCount}`}</span>
				</div>
			</div>
		</div>
	);
}
